/**
 * Video identity capture helpers: the identity service (Jibit Alpha KYC) needs
 * one short video (≤10MB) **with sound**, in which the person reads the
 * sentence the API gives (`kycVideoLine`) aloud — face, speech and liveness
 * are all checked from it.
 */
export const MAX_CLIP_BYTES = 10 * 1024 * 1024;
export const RECORD_SECONDS = 10;
export const MIN_RECORD_SECONDS = 4;

export type FaceCapture = { clip: File };

/** WebM (VP8 + Opus) where supported (Chrome/Firefox/Android); MP4 on iOS Safari. */
export function pickRecorderMime(): string | undefined {
  if (typeof MediaRecorder === "undefined") return undefined;
  const candidates = [
    "video/webm;codecs=vp8,opus",
    "video/webm;codecs=vp9,opus",
    "video/webm",
    "video/mp4;codecs=avc1,mp4a",
    "video/mp4",
  ];
  return candidates.find((t) => MediaRecorder.isTypeSupported(t));
}

function extForMime(mime: string): string {
  const m = mime.toLowerCase();
  if (m.includes("quicktime")) return "mov";
  if (m.includes("mp4")) return "mp4";
  if (m.includes("matroska")) return "mkv";
  return "webm";
}

export function assertClipSize(blob: Blob): void {
  if (blob.size < 1024) {
    throw new Error("ویدیو خیلی کوتاه یا خالی است. دوباره ضبط کنید.");
  }
  if (blob.size > MAX_CLIP_BYTES) {
    throw new Error("حجم ویدیو بیش از ۱۰ مگابایت است؛ ویدیوی کوتاه‌تری (چند ثانیه) ضبط کنید.");
  }
}

export function buildFaceCapture(clip: Blob, clipMime: string): FaceCapture {
  const mime = (clipMime || clip.type || "video/webm").split(";")[0]!.trim();
  return { clip: new File([clip], `kyc-video.${extForMime(mime)}`, { type: mime }) };
}

/** Picked-file path (gallery / native camera): validate size. */
export async function faceCaptureFromFile(file: File): Promise<FaceCapture> {
  assertClipSize(file);
  return buildFaceCapture(file, file.type || "video/mp4");
}

export function faceCaptureFormData(capture: FaceCapture): FormData {
  const fd = new FormData();
  fd.append("video", capture.clip, capture.clip.name);
  return fd;
}

/** «۱۳۷۰۰۶۲۱» / «1370/6/21» → «1370/06/21» while typing; server validates the date. */
export function formatBirthDateInput(raw: string): string {
  const digits = raw
    .replace(/[۰-۹]/g, (ch) => String(ch.charCodeAt(0) - 0x06f0))
    .replace(/[٠-٩]/g, (ch) => String(ch.charCodeAt(0) - 0x0660))
    .replace(/[^\d/]/g, "");
  if (digits.includes("/")) return digits.slice(0, 10);
  const d = digits.slice(0, 8);
  if (d.length <= 4) return d;
  if (d.length <= 6) return `${d.slice(0, 4)}/${d.slice(4)}`;
  return `${d.slice(0, 4)}/${d.slice(4, 6)}/${d.slice(6)}`;
}

export function isCompleteBirthDate(value: string): boolean {
  return /^1[34]\d{2}\/\d{1,2}\/\d{1,2}$/.test(value.trim());
}

/** Smart national card serial while typing: Western digits + upper-case Latin letters, 10 max. */
export function formatCardSerialInput(raw: string): string {
  return raw
    .replace(/[۰-۹]/g, (ch) => String(ch.charCodeAt(0) - 0x06f0))
    .replace(/[٠-٩]/g, (ch) => String(ch.charCodeAt(0) - 0x0660))
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "")
    .slice(0, 10);
}

export function isCompleteCardSerial(value: string): boolean {
  return /^[A-Z0-9]{10}$/.test(value);
}
